import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TicketService, Ticket } from '../../services/ticket.service';
import { TicketCommentService, TicketComment, TicketCommentPage, TicketCommentDTO } from '../../services/ticket-comment.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { GenericConfirmationDialogComponent } from '../../shared/delete-confirmation-dialog/generic-confirmation-dialog.component';

@Component({
    selector: 'app-ticket-detail',
    templateUrl: './ticket-detail.component.html',
    styleUrls: ['./ticket-detail.component.sass'],
    standalone: false
})
export class TicketDetailComponent implements OnInit {
  ticket: Ticket | null = null;
  comments: TicketCommentDTO[] = [];
  commentForm: UntypedFormGroup;
  isAddingComment = false;
  pageSize = 10;
  totalElements = 0;
  currentPage = 0;
  statuses = ['OPEN', 'IN_PROGRESS', 'CLOSED'];
  isStatusDisabled = false;

  constructor(
    private route: ActivatedRoute,
    private ticketService: TicketService,
    private ticketCommentService: TicketCommentService,
    private fb: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    this.commentForm = this.fb.group({
      body: ['', [Validators.required, Validators.minLength(1)]]
    });
  }

  ngOnInit(): void {
    const ticketId = Number(this.route.snapshot.paramMap.get('ticketId'));
    this.loadTicket(ticketId);
    this.loadComments(ticketId);
  }

  private loadTicket(ticketId: number): void {
    this.ticketService.getTicket(ticketId).subscribe({
      next: (ticket) => {
        this.ticket = ticket;
        this.isStatusDisabled = ticket.status === 'CLOSED';
      },
      error: (error) => {
        this.snackBar.open('Erro ao carregar ticket', 'Fechar', { duration: 3000 });
      }
    });
  }

  private loadComments(ticketId: number, page: number = 0): void {
    this.ticketCommentService.getComments(ticketId, page, this.pageSize).subscribe({
      next: (page: TicketCommentPage) => {
        this.comments = page._embedded.ticketCommentDTOList;
        this.totalElements = page.page.totalElements;
        this.currentPage = page.page.number;
      },
      error: (error) => {
        this.snackBar.open('Erro ao carregar comentários', 'Fechar', { duration: 3000 });
      }
    });
  }

  onPageChange(event: PageEvent): void {
    if (this.ticket) {
      this.loadComments(this.ticket.id, event.pageIndex);
    }
  }

  startAddingComment(): void {
    this.isAddingComment = true;
  }

  cancelAddingComment(): void {
    this.isAddingComment = false;
    this.commentForm.reset();
  }

  addComment(): void {
    if (this.commentForm.valid && this.ticket) {
      const request = {
        body: this.commentForm.get('body')?.value
      };

      this.ticketCommentService.createComment(this.ticket.id, request).subscribe({
        next: () => {
          this.snackBar.open('Comentário adicionado com sucesso', 'Fechar', { duration: 3000 });
          this.isAddingComment = false;
          this.commentForm.reset();
          this.loadComments(this.ticket!.id, this.currentPage);
        },
        error: (error) => {
          this.snackBar.open('Erro ao adicionar comentário', 'Fechar', { duration: 3000 });
        }
      });
    }
  }

  onStatusChange(event: MatSelectChange): void {
    if (!this.ticket) return;

    if (event.value === 'CLOSED') {
      const dialogRef = this.dialog.open(GenericConfirmationDialogComponent, {
        data: {
          title: 'Confirmar fechamento',
          message: 'Ao fechar o ticket, não será possível alterar seu status novamente. Deseja continuar?',
          confirmText: 'Fechar',
          cancelText: 'Cancelar'
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.updateTicketStatus(event.value);
        } else {
          // Reset to previous value
          event.source.value = this.ticket!.status;
        }
      });
    } else {
      this.updateTicketStatus(event.value);
    }
  }

  private updateTicketStatus(status: 'OPEN' | 'IN_PROGRESS' | 'CLOSED'): void {
    if (!this.ticket) return;

    this.ticketService.updateTicketStatus(this.ticket.id, { status }).subscribe({
      next: (updatedTicket) => {
        this.ticket = updatedTicket;
        this.isStatusDisabled = status === 'CLOSED';
        this.snackBar.open('Status atualizado com sucesso', 'Fechar', { duration: 3000 });
      },
      error: (error) => {
        this.snackBar.open('Erro ao atualizar status', 'Fechar', { duration: 3000 });
        // Reset to previous value
        if (this.ticket) {
          this.ticket.status = this.ticket.status;
        }
      }
    });
  }
}
